/**
 * Colunas das demonstrações do período: cada coluna é um bloco da periodicidade
 * (quinzena, mês, bimestre...) somado do que cada mês gravou na revisão. Nada é
 * recalculado: a quinzena vem pronta da revisão, o mês inteiro vem do detalhe do mês.
 * DRE e fluxo somam; o balanço é o do fim do último pedaço de cada coluna.
 */
import {
  Dre,
  IDemonstracoesDoPeriodo,
  IGruposDoBalanco,
  ILinhaDoModelo,
  IMesDoPeriodo,
} from '../../../../hooks/useFechamentoContabilMensal';
import { IPeriodo, MESES_CURTOS, Periodicidade, TAMANHO } from './regrasDoPeriodoDaEmpresa';

/** Fluxos do caixa que se somam; caixa inicial e final vêm das pontas. */
export const FLUXOS = [
  'lucro_liquido',
  'depreciacao',
  'capital_de_giro',
  'operacional',
  'investimento',
  'financiamento',
  'sem_efeito_caixa',
];

export interface IPosicao {
  balanco: IGruposDoBalanco | null;
  modelo: ILinhaDoModelo[];
  /** "set/26" ou "1ª q. set/26" */
  rotulo: string;
}

/** Um mês inteiro ou uma metade dele. */
export interface IPedaco {
  ano: number;
  mes: number;
  /** 0 = 1ª quinzena, 1 = 2ª, null = mês inteiro */
  metade: 0 | 1 | null;
  dre: Dre;
  dfc: Record<string, number>;
  contas: Record<string, number>;
  fim: IPosicao;
}

export interface IColuna {
  chave: string;
  ano: number;
  rotulo: string;
  /** o intervalo não cobre o bloco inteiro */
  parcial: boolean;
  pedacos: IPedaco[];
  dre: Dre;
  dfc: Record<string, number>;
  contas: Record<string, number>;
  fim: IPosicao | null;
}

export interface IFalta {
  rotulo: string;
  motivo: string;
}

export interface IColunasDoPeriodo {
  visao: Exclude<Periodicidade, 'personalizado'>;
  colunas: IColuna[];
  faltam: IFalta[];
  /** posição do fim do bloco que termina onde a última coluna começa */
  anterior: IPosicao | null;
  /** meses (0 a 11) que existem em todos os anos marcados: base da variação entre anos */
  mesesComuns: number;
}

const yy = (ano: number) => String(ano).slice(2);
const doisDigitos = (n: number) => String(n).padStart(2, '0');
const MESES_DO_BLOCO = { mensal: 1, bimestral: 2, trimestral: 3, semestral: 6, anual: 12 };

/** A visão das colunas: o personalizado vai mês a mês. */
export const visaoDe = (tipo: Periodicidade): IColunasDoPeriodo['visao'] =>
  tipo === 'personalizado' ? 'mensal' : tipo;

const rotuloDoBloco = (
  visao: IColunasDoPeriodo['visao'],
  ano: number,
  mes: number,
  metade: number,
) => {
  if (visao === 'quinzenal') return `${metade + 1}ª q. ${MESES_CURTOS[mes]}/${yy(ano)}`;
  if (visao === 'mensal') return `${MESES_CURTOS[mes]}/${yy(ano)}`;
  if (visao === 'bimestral') return `${Math.floor(mes / 2) + 1}B/${yy(ano)}`;
  if (visao === 'trimestral') return `${Math.floor(mes / 3) + 1}T/${yy(ano)}`;
  if (visao === 'semestral') return `${Math.floor(mes / 6) + 1}S/${yy(ano)}`;
  return String(ano);
};

/** Soma chave a chave de vários conjuntos de valores. */
const somar = (conjuntos: (Record<string, number> | null | undefined)[]) => {
  const saida: Record<string, number> = {};
  conjuntos.forEach(c =>
    Object.entries(c ?? {}).forEach(([k, v]) => {
      saida[k] = (saida[k] || 0) + (v || 0);
    }),
  );
  return saida;
};

const MOTIVO_SEM_QUINZENA = {
  semRevisao: 'sem revisão no mês',
  antiga: 'revisado antes do cálculo da quinzena (competência fechada)',
  semRazao: 'sem razão carregado: não dá para separar a quinzena',
};

/**
 * Pedaço de um mês: o mês inteiro sai do que o mês gravou; a metade, da quinzena
 * gravada. Devolve o motivo quando o dado não existe.
 */
const pedacoDoMes = (
  ano: number,
  mes: number,
  metade: 0 | 1 | null,
  dados: IMesDoPeriodo | undefined,
): IPedaco | string => {
  if (!dados || !dados.dre) return MOTIVO_SEM_QUINZENA.semRevisao;
  const rotuloDoMes = `${MESES_CURTOS[mes]}/${yy(ano)}`;
  const doMes: IPosicao = { balanco: dados.balanco, modelo: dados.modelo, rotulo: rotuloDoMes };
  if (metade == null)
    return {
      ano,
      mes,
      metade,
      dre: dados.dre,
      dfc: dados.dfc ?? {},
      contas: dados.dre_contas,
      fim: doMes,
    };
  if (dados.quinzenas == null) return MOTIVO_SEM_QUINZENA.antiga;
  const q = dados.quinzenas[metade];
  if (!q) return MOTIVO_SEM_QUINZENA.semRazao;
  return {
    ano,
    mes,
    metade,
    dre: q.dre,
    dfc: q.dfc,
    contas: q.dre_contas,
    // o fim da 2ª quinzena é o fim do mês, que tem o balanço completo
    fim:
      metade === 0
        ? { balanco: q.balanco, modelo: q.modelo ?? [], rotulo: `1ª q. ${rotuloDoMes}` }
        : doMes,
  };
};

const mesDosDados = (
  porAno: Record<number, IDemonstracoesDoPeriodo | undefined>,
  ano: number,
  mes: number,
) => porAno[ano]?.por_mes.find(m => m.competencia === `${ano}-${doisDigitos(mes + 1)}`);

/** Fim do mês anterior a `mes`: do próprio período ou do balanço anterior que veio junto. */
const fimDoMesAnterior = (
  porAno: Record<number, IDemonstracoesDoPeriodo | undefined>,
  ano: number,
  mes: number,
): IPosicao | null => {
  const anoAntes = mes === 0 ? ano - 1 : ano;
  const mesAntes = mes === 0 ? 11 : mes - 1;
  const rotulo = `${MESES_CURTOS[mesAntes]}/${yy(anoAntes)}`;
  const dados = mesDosDados(porAno, anoAntes, mesAntes);
  if (dados?.balanco) return { balanco: dados.balanco, modelo: dados.modelo, rotulo };
  const veio = porAno[ano]?.balanco_anterior;
  const primeiro = porAno[ano]?.de;
  if (veio?.balanco && primeiro === `${ano}-${doisDigitos(mes + 1)}`)
    return { balanco: veio.balanco, modelo: veio.modelo, rotulo };
  return null;
};

export const montarColunas = (
  periodo: IPeriodo,
  porAno: Record<number, IDemonstracoesDoPeriodo | undefined>,
): IColunasDoPeriodo => {
  const visao = visaoDe(periodo.tipo);
  const colunas: IColuna[] = [];
  const faltam: IFalta[] = [];
  const quinzenasNoBloco = TAMANHO[visao];

  periodo.anos.forEach(ano => {
    for (let mes = Math.floor(periodo.h1 / 2); mes <= Math.floor(periodo.h2 / 2); mes += 1) {
      const temA = mes * 2 >= periodo.h1;
      const temB = mes * 2 + 1 <= periodo.h2;
      const metades: (0 | 1 | null)[] = [];
      if (temA && temB && visao !== 'quinzenal') metades.push(null);
      else {
        if (temA) metades.push(0);
        if (temB) metades.push(1);
      }
      const dados = mesDosDados(porAno, ano, mes);
      metades.forEach(metade => {
        const chave =
          visao === 'quinzenal'
            ? `${ano}:${mes}:${metade}`
            : `${ano}:${Math.floor(mes / MESES_DO_BLOCO[visao])}`;
        let coluna = colunas[colunas.length - 1];
        if (!coluna || coluna.chave !== chave) {
          coluna = {
            chave,
            ano,
            rotulo: rotuloDoBloco(visao, ano, mes, metade ?? 0),
            parcial: false,
            pedacos: [],
            dre: {},
            dfc: {},
            contas: {},
            fim: null,
          };
          colunas.push(coluna);
        }
        const pedaco = pedacoDoMes(ano, mes, metade, dados);
        if (typeof pedaco === 'string') {
          const rotulo =
            metade == null
              ? `${MESES_CURTOS[mes]}/${yy(ano)}`
              : `${metade + 1}ª q. ${MESES_CURTOS[mes]}/${yy(ano)}`;
          faltam.push({ rotulo, motivo: pedaco });
          return;
        }
        coluna.pedacos.push(pedaco);
      });
    }
  });

  // quinzenas do intervalo dentro do bloco: menos que o bloco inteiro é parcial
  const quinzenasDoBloco = (chave: string, ano: number) => {
    let n = 0;
    for (let h = periodo.h1; h <= periodo.h2; h += 1)
      if (`${ano}:${Math.floor(Math.floor(h / 2) / MESES_DO_BLOCO[visao])}` === chave) n += 1;
    return n;
  };
  const comDados = colunas
    .filter(c => c.pedacos.length)
    .map(c => {
      const primeiro = c.pedacos[0];
      const ultimo = c.pedacos[c.pedacos.length - 1];
      const fluxos = somar(c.pedacos.map(p => p.dfc));
      return {
        ...c,
        parcial: visao !== 'quinzenal' && quinzenasDoBloco(c.chave, c.ano) < quinzenasNoBloco,
        dre: somar(c.pedacos.map(p => p.dre)),
        contas: somar(c.pedacos.map(p => p.contas)),
        dfc: {
          ...Object.fromEntries(FLUXOS.map(k => [k, fluxos[k] || 0])),
          caixa_inicial: primeiro.dfc.caixa_inicial ?? 0,
          caixa_final: ultimo.dfc.caixa_final ?? 0,
        },
        fim: ultimo.fim,
      };
    });
  const ultima = comDados[comDados.length - 1];
  let anterior: IPosicao | null = null;
  if (ultima) {
    const p = ultima.pedacos[0];
    if (p.metade === 1) {
      const meio = pedacoDoMes(p.ano, p.mes, 0, mesDosDados(porAno, p.ano, p.mes));
      anterior = typeof meio === 'string' ? null : meio.fim;
    } else anterior = fimDoMesAnterior(porAno, p.ano, p.mes);
  }

  // variação entre anos: só os meses que existem em todos
  const ultimosMeses = periodo.anos.map(ano =>
    Math.max(
      -1,
      ...(porAno[ano]?.por_mes ?? [])
        .filter(m => m.dre)
        .map(m => Number(m.competencia.slice(5, 7)) - 1),
    ),
  );

  return {
    visao,
    colunas: comDados,
    faltam,
    anterior,
    mesesComuns: Math.max(0, Math.min(...ultimosMeses) + 1),
  };
};

/** Soma de uma chave nas colunas de um ano, só com os meses comuns aos anos marcados. */
export const totalComparavel = (
  colunas: IColuna[],
  ano: number,
  mesesComuns: number,
  valor: (p: IPedaco) => number,
): number =>
  colunas
    .filter(c => c.ano === ano)
    .flatMap(c => c.pedacos)
    .filter(p => p.mes < mesesComuns)
    .reduce((s, p) => s + valor(p), 0);
